import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { QRCodeSVG } from "qrcode.react"; // npm i qrcode.react
import { CheckCircle2, Clock, Copy, Loader2, QrCode, RefreshCw, TimerReset } from "lucide-react";
import { getStoreAuth } from "./StorePanelPrivateRoute";
import { BILL_VALIDITY_MS, MAX_BILL_AMOUNT, buildBillLink, formatCountdown, useCountdown } from "../../utils/storeBill"; // adjust path
import { clearStoreBill, createStoreBill, fetchStoreBillStatus } from "../../store/storeSlices/storeBillSlice"; // adjust path

const POLL_MS = 7000; // auto-check the payment status while the QR is live

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const formatTime = (ms) =>
    new Date(ms).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

function DetailRow({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-center justify-between gap-4 py-2 text-sm">
            <span className="text-gray-500">{label}</span>
            <span className="truncate font-semibold text-gray-800">{value}</span>
        </div>
    );
}

export default function StorePanelBillQR() {
    const store = getStoreAuth()?.store;
    const storeId = parseInt(store?.id, 10);
    const dispatch = useDispatch();

    // form
    const [amount, setAmount] = useState("");
    const [invoice, setInvoice] = useState("");
    const [phone, setPhone] = useState("");
    const [limitHit, setLimitHit] = useState(false);

    // generated bill (redux) - status: pending | initiated | paid | cancelled | expired
    const { bill, status, creating, createError, checking, lastChecked } = useSelector((s) => s.storeBill);
    const [copied, setCopied] = useState(false);

    const remaining = useCountdown(bill?.expiresAt);
    const expired = !!bill && (status === "expired" || (status === "pending" && remaining <= 0));
    const urgent = remaining > 0 && remaining <= 60 * 1000;
    const initiated = status === "initiated"; // customer has tapped Pay: hide the QR until the result is known

    const amountValue = parseFloat(amount) || 0;
    const phoneInvalid = phone.length > 0 && phone.length !== 10;
    const canGenerate = amountValue > 0 && !phoneInvalid && !!storeId && !creating;

    /* ── auto-check status while the QR is live (stops once paid / cancelled / expired) ── */
    useEffect(() => {
        if (!bill?.ref || expired || !["pending", "initiated"].includes(status)) return undefined;
        const id = setInterval(() => dispatch(fetchStoreBillStatus({ ref: bill.ref, silent: true })), POLL_MS);
        return () => clearInterval(id);
    }, [bill?.ref, status, expired, dispatch]);

    /* ── inputs ── */
    const handleAmount = (e) => {
        const raw = e.target.value.replace(/[^\d.]/g, "");
        const [whole, ...rest] = raw.split(".");
        const cleaned = rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
        if ((parseFloat(cleaned) || 0) > MAX_BILL_AMOUNT) {
            setLimitHit(true);
            return;
        }
        setLimitHit(false);
        setAmount(cleaned.slice(0, 10));
    };

    /* ── actions ── */
    // hits POST /store-pay/create; the bill (and its server-issued ref) lands in redux, which switches the view to the QR
    const generate = () => {
        if (!canGenerate) return;
        dispatch(createStoreBill({ storeId, amount: amountValue }));
    };

    const refreshStatus = () => {
        if (!bill || checking) return;
        dispatch(fetchStoreBillStatus({ ref: bill.ref }));
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(buildBillLink(bill));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard blocked - ignore */
        }
    };

    // back to the form with the same values filled in, so a new QR is one tap away
    const regenerate = () => dispatch(clearStoreBill());

    const newBill = () => {
        dispatch(clearStoreBill());
        setAmount("");
        setInvoice("");
        setPhone("");
    };

    /* ── paid ── */
    if (bill && status === "paid") {
        return (
            <div className="mx-auto max-w-xl rounded-2xl border bg-white p-8 text-center shadow-sm">
                <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
                <h1 className="mt-4 text-xl font-bold text-emerald-600">Payment received</h1>
                <p className="mt-1 text-sm text-gray-500">
                    {formatINR(bill.amount)} bill{bill.invoice ? ` · Invoice ${bill.invoice}` : ""} was paid by the customer.
                </p>
                <div className="mx-auto mt-5 max-w-xs rounded-xl bg-emerald-50 px-4 py-3">
                    <p className="text-xs font-medium text-emerald-700">Reference no.</p>
                    <p className="mt-0.5 text-lg font-bold tracking-widest text-emerald-800">{bill.ref}</p>
                </div>
                <button
                    onClick={newBill}
                    className="mt-6 rounded-lg bg-[#F4A406] px-6 py-2 text-sm font-semibold text-white hover:bg-[#D98F05]"
                >
                    Create new bill
                </button>
            </div>
        );
    }

    /* ── QR view ── */
    if (bill) {
        return (
            <div className="mx-auto max-w-md space-y-4">
                <div>
                    <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-800">
                        <QrCode size={22} className="text-[#F4A406]" /> {initiated ? "Payment in progress" : "Scan to pay"}
                    </h1>
                    <p className="text-sm text-gray-500">
                        {initiated
                            ? "The customer is completing the payment. This page updates automatically."
                            : "Ask the customer to scan this QR with the app. Offers apply automatically."}
                    </p>
                </div>

                <section className="rounded-2xl border bg-white p-5 text-center shadow-sm">
                    <p className="text-sm text-gray-500">{store?.store_name}</p>
                    <p className="mt-1 text-3xl font-bold text-gray-900">{formatINR(bill.amount)}</p>

                    {initiated ? (
                        <div className="mx-auto mt-6 flex flex-col items-center rounded-xl bg-[#F4A406]/10 px-4 py-8">
                            <Loader2 size={44} className="animate-spin text-[#F4A406]" />
                            <p className="mt-4 text-base font-semibold text-gray-900">Payment initiated by customer</p>
                            <p className="mt-1 text-sm text-gray-500">Waiting for the payment to complete…</p>
                        </div>
                    ) : (
                        <>
                        <div className="relative mx-auto mt-5 w-fit rounded-xl border border-gray-200 bg-white p-3">
                            <div className={expired ? "opacity-20 blur-[2px]" : ""}>
                                <QRCodeSVG value={buildBillLink(bill)} size={224} level="M" />
                            </div>
                            {expired && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-rose-600">
                                    <TimerReset size={30} />
                                    <p className="text-sm font-bold">QR expired</p>
                                </div>
                            )}
                        </div>

                        <div
                            className={`mx-auto mt-4 flex w-fit items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold ${
                                expired
                                    ? "bg-rose-50 text-rose-600"
                                    : urgent
                                    ? "bg-orange-50 text-orange-600"
                                    : "bg-[#F4A406]/10 text-[#B87A00]"
                            }`}
                        >
                            <Clock size={15} />
                            {expired ? "Expired" : `Expires in ${formatCountdown(remaining)}`}
                        </div>

                        </>
                    )}

                    {status === "cancelled" && <p className="mt-3 text-sm font-medium text-rose-600">This bill was cancelled.</p>}
                    {lastChecked && status === "pending" && (
                        <p className="mt-3 text-xs text-gray-500">No payment yet · checked at {formatTime(lastChecked)}</p>
                    )}

                    <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                        {expired ? (
                            <button
                                onClick={regenerate}
                                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#F4A406] px-5 py-3 text-sm font-semibold text-white hover:bg-[#D98F05]"
                            >
                                <TimerReset size={16} /> Generate new QR
                            </button>
                        ) : (
                            <button
                                onClick={refreshStatus}
                                disabled={checking}
                                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#F4A406] px-5 py-3 text-sm font-semibold text-white hover:bg-[#D98F05] disabled:bg-gray-300"
                            >
                                {checking ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                                Refresh status
                            </button>
                        )}
                        {!initiated && (
                            <button
                                onClick={copyLink}
                                className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                <Copy size={16} /> {copied ? "Copied" : "Copy link"}
                            </button>
                        )}
                    </div>
                    {expired && (
                        <button onClick={refreshStatus} disabled={checking} className="mt-3 text-xs font-medium text-gray-500 underline">
                            {checking ? "Checking…" : "Customer already paid? Check status"}
                        </button>
                    )}
                </section>

                <section className="rounded-2xl border bg-white px-5 py-2 shadow-sm divide-y divide-gray-100">
                    <DetailRow label="Bill ref" value={bill.ref} />
                    {/* <DetailRow label="Invoice no." value={bill.invoice} />
                    <DetailRow label="Customer mobile" value={bill.phone} /> */}
                    <DetailRow label="Created at" value={formatTime(bill.createdAt)} />
                </section>

                <button onClick={newBill} className="w-full rounded-lg border border-gray-200 py-2.5 text-sm text-gray-600 hover:bg-gray-50">
                    Cancel and create another bill
                </button>
            </div>
        );
    }

    /* ── form ── */
    return (
        <div className="mx-auto max-w-md space-y-5">
            <div>
                <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-800">
                    <QrCode size={22} className="text-[#F4A406]" /> Create Bill & Request Payment
                </h1>
                <p className="text-sm text-gray-500">Enter the bill amount to generate a payment QR for the customer.</p>
            </div>

            <section className="space-y-4 rounded-2xl border bg-white p-5 shadow-sm">
                <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Bill amount (₹)</label>
                    <input
                        type="text"
                        inputMode="decimal"
                        autoFocus
                        value={amount}
                        onChange={handleAmount}
                        onKeyDown={(e) => e.key === "Enter" && generate()}
                        placeholder="e.g. 1500"
                        className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-3 text-xl font-bold outline-none focus:border-[#F4A406]"
                    />
                    {limitHit && <p className="mt-1.5 text-xs text-orange-600">Maximum bill amount is {formatINR(MAX_BILL_AMOUNT)}</p>}
                </div>

                {/* <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Invoice number <span className="font-normal normal-case text-gray-400">(optional)</span>
                    </label>
                    <input
                        type="text"
                        maxLength={30}
                        value={invoice}
                        onChange={(e) => setInvoice(e.target.value)}
                        placeholder="Your bill / invoice number"
                        className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#F4A406]"
                    />
                </div>

                <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Customer mobile <span className="font-normal normal-case text-gray-400">(optional)</span>
                    </label>
                    <input
                        type="tel"
                        inputMode="numeric"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        placeholder="10-digit mobile number"
                        className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#F4A406]"
                    />
                    {phoneInvalid ? (
                        <p className="mt-1.5 text-xs text-rose-600">Enter a valid 10-digit mobile number</p>
                    ) : (
                        <p className="mt-1.5 text-xs text-gray-400">If added, only this customer can pay the bill.</p>
                    )}
                </div> */}

                <button
                    onClick={generate}
                    disabled={!canGenerate}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#F4A406] px-5 py-3 text-sm font-semibold text-white hover:bg-[#D98F05] disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                    {creating ? <Loader2 size={17} className="animate-spin" /> : <QrCode size={17} />}
                    {creating ? "Generating…" : "Generate QR"}
                </button>
                {createError && <p className="text-center text-sm text-rose-600">{createError}</p>}
                <p className="text-center text-xs text-gray-400">
                    The QR stays valid for {Math.round(BILL_VALIDITY_MS / 60000)} minutes.
                </p>
            </section>
        </div>
    );
}